import type { Drawer as DrawerPrimitive } from "vaul";
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  PropsWithChildren,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import { TButtonProps } from "@/ui/components/primitives";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const DRAWER_CLOSE_ICON: TIconKey = "cross";

export const DRAWER_CONTENT_CLASS =
  "fixed inset-x-0 bottom-0 z-50 mt-24 flex max-h-[90dvh] min-h-0 flex-col overflow-hidden rounded-t-sys-lg border-t border-solid border-dialog bg-surface-default shadow-lg outline-none";

export const DRAWER_CONTAINER_CLASS = "flex flex-col min-h-0 flex-1 gap-4 p-6";

export const DRAWER_OVERLAY_CLASS =
  "fixed inset-0 z-40 bg-sys-opacity-dark-20";

export const DRAWER_HEADER_CLASS = "flex w-full shrink-0 flex-col gap-2";

export const DRAWER_FOOTER_CLASS = "flex shrink-0 gap-2";

export const DRAWER_FIXED_FOOTER_CLASS = "w-full shrink-0 border-t border-solid border-dialog bg-surface-default shadow-sm";

export const DRAWER_FIXED_FOOTER_CONTAINER_CLASS = "flex gap-2 px-6 pb-6 pt-4";

export const DRAWER_FOOTER_ORIENTATION_CLASS_MAP = {
  horizontal: "flex-row-reverse",
  vertical: "flex-col",
} as const;

export const DRAWER_CLOSE_CLASS =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sys-md border-none bg-transparent text-foreground transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0";

/*-------------------------------------
|  Types
-------------------------------------*/

export type TDrawerFooterCtas = {
  primary?: TButtonProps;
  secondary?: TButtonProps;
};

export type TDrawerActionsOrientation = keyof typeof DRAWER_FOOTER_ORIENTATION_CLASS_MAP;

export type TDrawerActionsPosition = "fixed" | "scrolling";

export type TDrawerRootProps = Omit<
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Root>,
  "direction"
> &
  PropsWithChildren & {
    title: string;
    description?: string;
    footer?: TDrawerFooterProps;
    actionsOrientation?: TDrawerActionsOrientation;
    actionsPosition?: TDrawerActionsPosition;
  };

export type TDrawerTriggerElement = ComponentRef<typeof DrawerPrimitive.Trigger>;
export type TDrawerTriggerProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Trigger>;
export type TDrawerOverlayElement = ComponentRef<typeof DrawerPrimitive.Overlay>;
export type TDrawerOverlayProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>;
export type TDrawerContentElement = ComponentRef<typeof DrawerPrimitive.Content>;
export type TDrawerContentProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>;
export type TDrawerCloseElement = ComponentRef<typeof DrawerPrimitive.Close>;
export type TDrawerCloseProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Close>;
export type TDrawerTitleElement = ComponentRef<typeof DrawerPrimitive.Title>;
export type TDrawerTitleProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>;
export type TDrawerDescriptionElement = ComponentRef<typeof DrawerPrimitive.Description>;
export type TDrawerDescriptionProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>;
export type TDrawerHeaderProps = HTMLAttributes<HTMLDivElement>;
export type TDrawerFooterProps = HTMLAttributes<HTMLDivElement> & {
  ctas?: TDrawerFooterCtas;
  actionsOrientation?: TDrawerActionsOrientation;
  actionsPosition?: TDrawerActionsPosition;
};
